import React, {useCallback, useEffect, useState} from "react";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {CustomerAddressTypes} from "../../../../core/constants/enums";
import AddressCard from "../../app-specific/address-card";
import {deepCopy} from "../../../../core/services/utils/utils";


const AddressSelectionDialog = ({open, setOpen, type, onSelect, customerId}) => {
    const [updating, setUpdating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [addresses, setAddresses] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Fetches the list of addresses and countries and provinces from the server and if the result of the api is
     * successful, sets the list.
     * @type {function(): Promise<void>}
     */
    const getCustomerAddresses = useCallback(async () => {
        setLoading(true);
        const response = await Api.getCustomerAddresses(customerId);
        if (!isMounted())
            return;
        setAddresses(response?.data?.data
            ?.filter(e => e.type?.id === type)
            ?.map(e => ({
                ...e,
                country: e?.province?.country,
            }))
        );
        setLoading(false);
    }, [customerId, type, isMounted]);

    /**
     * Fetches the list of addresses and countries and provinces when the dialog is opened.
     */
    useEffect(() => {
        if (open) {
            void getCustomerAddresses();
        }
    }, [open, getCustomerAddresses])

    /**
     * Invokes the [onSelect] callback with the selected address.
     *
     * - if the response of the callback is successful, closes the dialog.
     * @type {function(*=): Promise<void>}
     */
    const onAddressSelected = useCallback(async (address) => {
        setUpdating(true);
        const _address = deepCopy(address);
        delete _address.country;
        const resultFlag = await onSelect(_address);
        if (!isMounted())
            return;
        setUpdating(false);
        if (resultFlag) {
            setOpen(false);
        }
    }, [onSelect, isMounted, setOpen]);

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'md'}
            className={'bound-dialog'}
        >
            <DialogTitle className={'p-0'} id="scroll-dialog-title">
                <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                    Address Selection
                </p>
            </DialogTitle>
            <DialogContent dividers className={'p-4'}>
                <div className={'d-flex flex-column w-100 mb-4'}>
                    <p className={'w-100 text-sm text-color-3'}>
                        Please select a
                        <span className={'text-color-1 mx-2 font-weight-semi-bold'}>
                            {type === CustomerAddressTypes.billing ? 'Billing' : 'Shipping'}
                        </span>
                        address for the customer's Order
                    </p>
                </div>
                <div className={'address-cards-container'}>
                    {
                        loading &&
                        Array(6).fill(null).map((_, e) => (
                            <Col key={e} xs={12} md={6} xl={4} className={'loading extraLarge'}>
                                <div/>
                            </Col>
                        ))
                    }
                    {
                        !loading && addresses?.length > 0 &&
                        addresses?.map(address => (
                            <AddressCard
                                key={address.id}
                                address={address}
                                disabled={updating}
                                onClick={() => onAddressSelected(address)}
                                readonly
                            />
                        ))
                    }
                    {
                        !loading && !addresses?.length &&
                        <Row className={'no-gutters align-items-center justify-content-center my-5 w-100'}>
                            <p className={'text-sm text-center text-color-3'}>
                                This customer does not have any {type === CustomerAddressTypes.shipping ? 'Shipping' : 'Billing'}
                                Addresses saved in their account
                            </p>
                        </Row>
                    }
                </div>
            </DialogContent>
            <DialogActions className={'p-0 w-100'}>
                <div className={'d-flex w-100'}>
                    <Button
                        disabled={!open}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}


export default AddressSelectionDialog;
